<script setup lang="ts">
import ProgressFrame from './layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import BaseCheckbox from '@/shared/components/BaseCheckbox.vue';
import DangerSVG from '@shared/assets/icon/danger.svg';
import PhoneSVG from '@shared/assets/icon/phone.svg';
import { useRouter } from 'vue-router';
import { computed, reactive } from 'vue';
import { useAuthStore } from '@/shared/stores/authStore';

const router = useRouter();
const authStore = useAuthStore();

const checks = reactive({
  ownership: false,
});

const allChecked = computed(() => checks.ownership);

const proceedToStartUpSolShare = () => {
  router.push({ name: 'StartupSolShare' });
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page">
    <template #title>Before proceeding please confirm the following</template>

    <div class="warning">
      <DangerSVG class="warning__icon" aria-hidden="true" />
      <span>Confirmation is essential for warranty.</span>
    </div>

    <div class="term-item term-item--first" data-tn="term-acct-ownership">
      <div>
        <p>
          I am the account owner, <strong>{{ authStore.email }}</strong
          >, and have completed the required Allume training.
        </p>
        <p>Sharing of accounts is not permitted.</p>
      </div>
      <BaseCheckbox
        v-model="checks.ownership"
        data-tn="checkbox-ownership"
        ariaLabel="Confirm account ownership and training"
      />
    </div>

    <div class="term-item term-item__contact-support">
      <div class="phone-icon" aria-hidden="true">
        <PhoneSVG />
      </div>
      <p>
        If you need support during commissioning process, contact Allume support using the details
        in the main menu.
      </p>
    </div>

    <div class="row end-of-page">
      <ButtonPrimary
        block
        class="confirm-button"
        :disabled="!allChecked"
        @click="proceedToStartUpSolShare"
        data-tn="confirm-button"
      >
        I confirm the above information is correct
      </ButtonPrimary>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  color: @color-warning;
  font-weight: @font-weight-medium;
  margin: 0 @content-side-margin 1.14rem;

  .warning__icon {
    width: 1.8rem;
    height: 1.8rem;
    flex-shrink: 0;
  }
}

.row {
  padding: 1.57rem @content-side-margin;
}

.term-item {
  &:extend(.row);
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.14rem;
  border-bottom: 1px solid @color-mid-tone;

  &.term-item--first {
    border-top: 1px solid @color-mid-tone;
  }

  p {
    margin-bottom: 0.57rem;

    &:last-of-type {
      margin-bottom: 0px;
    }
  }
}

.term-item__contact-support {
  background-color: inherit;
  align-items: flex-start;

  .phone-icon {
    width: 24px;
    height: 24px;
    background-color: @color-primary;
    color: white;
    border-radius: 50%;
    margin-top: 3px;
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(svg) {
      width: 24px;
      height: 14px;
    }
  }
}

.confirm-button {
  container-type: size;

  :deep(.btn-main-content) {
    /* The font size will try to be 4.5% of the button's width,
     but will not go smaller than 0.7rem or larger than 1rem. */
    font-size: clamp(0.7rem, 4.5cqw, 1rem);
  }
}

.end-of-page {
  background-color: white;
  flex-grow: 1;
}
</style>
